import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { BiArea, BiBed, BiBath } from 'react-icons/bi';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import './LatestProperties.css';

const LatestProperties = ({ properties, title }) => {
  const [displayProperties, setDisplayProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(6);
  
  const sectionTitle = title || "أحدث العقارات";

  // دالة لتحويل روابط Imgur إلى روابط مباشرة
  const convertImgurUrl = (url) => {
    if (!url || typeof url !== 'string') {
      return 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
    }
    
    let cleanUrl = url.trim();
    
    // إذا كان الرابط من Imgur
    if (cleanUrl.includes('imgur.com')) {
      // استخراج معرف الصورة
      const match = cleanUrl.match(/imgur\.com\/(\w+)/);
      if (match && match[1]) {
        const imageId = match[1];
        // إرجاع الرابط المباشر للصورة
        return `https://i.imgur.com/${imageId}.jpg`;
      }
    }
    
    return cleanUrl;
  };

  // دالة لتحليل الحقول JSON
  const parseJSONField = (field) => {
    if (!field) return { ar: '' };
    if (typeof field === 'object') return field;
    try {
      return JSON.parse(field);
    } catch (error) {
      return { ar: field || '' };
    }
  };

  useEffect(() => {
    if (properties) {
      setDisplayProperties(properties);
      setLoading(false);
    } else {
      fetchLatestProperties();
    }
  }, [properties]);

  const fetchLatestProperties = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(12);
      
      if (error) {
        console.error('Error fetching properties:', error);
        setDisplayProperties([]);
        return;
      }
      
      setDisplayProperties(data || []);
      
    } catch (err) {
      console.error('Error:', err);
      setDisplayProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 3);
  };

  const getTypeText = (type) => {
    const types = {
      apartment: 'شقة ',
      house: 'بيت ',
      land: 'أرض '
    };
    return types[type] || 'عقار';
  };

  const getPropertyImage = (property) => {
    if (!property?.images) {
      return 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
    }
    
    let firstImage = '';
    
    // إذا كانت images مصفوفة
    if (Array.isArray(property.images)) {
      if (property.images.length > 0) {
        firstImage = property.images[0];
      }
    }
    // إذا كانت نص JSON
    else if (typeof property.images === 'string') {
      try {
        const parsed = JSON.parse(property.images);
        if (Array.isArray(parsed) && parsed.length > 0) {
          firstImage = parsed[0];
        }
      } catch (error) {
        console.error('Error parsing images:', error);
      }
    }
    
    // إذا لم توجد صورة
    if (!firstImage) {
      return 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
    }
    
    // تحويل رابط Imgur إذا لزم
    return convertImgurUrl(firstImage);
  };

  const formatPrice = (price, ownership) => {
    const formatted = price?.toLocaleString() || '0';
    return ownership === 'sale' 
      ? `₪${formatted}`
      : `₪${formatted}/شهر`;
  };

  // دالة لمعالجة أخطاء تحميل الصور
  const handleImageError = (e, imgUrl) => {
    console.error(`Failed to load image: ${imgUrl}`);
    e.target.onerror = null;
    
    if (imgUrl.includes('imgur.com')) {
      const match = imgUrl.match(/imgur\.com\/(\w+)/);
      if (match && match[1]) {
        const imageId = match[1];
        e.target.src = `https://i.imgur.com/${imageId}.jpg`;
      } else {
        e.target.src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
      }
    } else {
      e.target.src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
    }
  };

  return (
    <section className="latest-properties py-5" dir="rtl">
      <Container>
        <div className="section-header text-end mb-5">
          <h2 className="main-title fw-bold">{sectionTitle}</h2>
          <div className="title-line mr-0 ml-auto"></div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">جاري تحميل العقارات...</p>
          </div>
        ) : displayProperties.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">لا توجد عقارات متاحة حالياً</p>
          </div>
        ) : (
          <>
            <Row className="g-4">
              {displayProperties.slice(0, visibleCount).map((property) => {
                const titleObj = parseJSONField(property.title);
                const cityObj = parseJSONField(property.city);
                const districtObj = parseJSONField(property.district);
                const propertyImage = getPropertyImage(property);
                
                return (
                  <Col key={property.id} lg={4} md={6}>
                    <Card className="property-card h-100 border-0 shadow-sm">
                      <div className="card-img-container">
                        <Card.Img 
                          variant="top" 
                          src={propertyImage} 
                          className="property-img" 
                          alt={titleObj.ar || 'عقار'}
                          onError={(e) => handleImageError(e, propertyImage)}
                        />
                        
                        <div className="badges-wrapper">
                          <span className={`badge-item ${property.ownership}`}>
                            {property.ownership === 'sale' ? 'للبيع' : 'للايجار'}
                          </span>
                        </div>

                        <div className="price-overlay-wrapper text-start">
                          <div className="main-price">
                            {formatPrice(property.price, property.ownership)}
                          </div>
                        </div>
                      </div>
                      
                      <Card.Body className="p-4 text-end">
                        <h4 className="property-title-text fw-bold mb-1">
                          {titleObj.ar || 'عقار بدون عنوان'}
                        </h4>
                        <p className="location-text text-muted mb-3">
                          <HiOutlineLocationMarker className="ms-1" />
                          {cityObj.ar || 'غير محدد'} - {districtObj.ar || 'غير محدد'}
                        </p>
                        
                        <div className="property-specs-row d-flex justify-content-end align-items-center gap-3 mb-3">
                          {property.type !== 'land' && (
                            <>
                              <div className="spec-item">
                                <span className="spec-value">{property.bedrooms || 0}</span> 
                                <BiBed size={22} className="ms-1" />
                              </div>
                              <div className="spec-item">
                                <span className="spec-value">{property.bathrooms || 0}</span> 
                                <BiBath size={22} className="ms-1" />
                              </div>
                            </>
                          )}
                          <div className="spec-item">
                            <span className="spec-value">m² {property.area || 0}</span> 
                            <BiArea size={22} className="ms-1" />
                          </div>
                        </div>

                        <div className="category-text mb-4 text-muted fw-bold">
                          {getTypeText(property.type)}
                        </div>

                        <div className="text-start">
                          <Button 
                            as={Link} 
                            to={`/property/${property.id}`}
                            className="details-btn-blue px-4 py-2"
                          >
                            تفاصيل
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {visibleCount < displayProperties.length && (
              <div className="text-center mt-5">
                <Button 
                  variant="outline-secondary" 
                  className="load-more-btn px-5 py-2"
                  onClick={handleLoadMore}
                >
                  عرض المزيد
                </Button>
              </div>
            )}
          </>
        )}
      </Container>
    </section>
  );
};

export default LatestProperties;